    // themes/index.js
import { createTheme } from '@mui/material/styles';
import blue from '@mui/material/colors/blue';
import green from '@mui/material/colors/green';
import red from '@mui/material/colors/red';
import grey from '@mui/material/colors/grey';
import { componentStyleOverrides } from './compStyleOverride';
import { themePalette } from './palette';
import { themeTypography } from './typography';

/**
 * Creates a Material-UI theme with optional customization.
 * 
 * @param {Object} [customization={}] - Customization options for the theme.
 * @param {Object} [customization.colors] - Custom colors to override the default palette.
 * @param {Object} [customization.typography] - Custom typography settings.
 * @param {Object} [customization.backgroundColor] - Custom background color.
 * @returns {Theme} - The configured Material-UI theme.
 */
export function createAppTheme(customization = {}) {
    // Default colors
    const defaultColors = {
        primary: blue,
        secondary: green,
        error: red,
        grey: {
            500: '#9e9e9e',
            700: '#616161',
            900: '#212121',
        },
        paper: '#ffffff',
        primaryLight: '#f0f0f0',
    };

    // Custom colors from customization or defaults
    const colors = {
        primary: customization.colors?.primary || defaultColors.primary,
        secondary: customization.colors?.secondary || defaultColors.secondary,
        error: customization.colors?.error || defaultColors.error,
        grey: customization.colors?.grey || defaultColors.grey,
        paper: customization.colors?.paper || defaultColors.paper,
        primaryLight: customization.colors?.primaryLight || defaultColors.primaryLight,
    };

    // Theme options
    const themeOptions = {
        palette: themePalette(customization) || {
            primary: { main: colors.primary[500] },
            secondary: { main: colors.secondary[500] },
            error: { main: colors.error[500] },
            background: {
                default: customization.backgroundColor || '#fff',
                paper: colors.paper,
            },
        },
        typography: themeTypography(customization) || {},
        components: componentStyleOverrides(customization) || {},
        backgroundDefault: customization.backgroundColor || '#fff', // Use default background color if not provided
        background: blue[50], // Example, replace with actual color
        darkTextPrimary: grey[700],
        darkTextSecondary: grey[500],
        textDark: grey[900],
        menuSelected: green[700],
        menuSelectedBack: green[100],
        divider: grey[200],
        ...customization, // Merge customizations with default options
    };

    return createTheme(themeOptions);
}

export default createAppTheme;
